import { useEffect, useState, useCallback } from "react";
import {
  Box, Button, Paper, Stack, Typography, Chip, Tooltip, Switch, Alert, MenuItem,
  Table, TableBody, TableCell, TableHead, TableRow, TableContainer, IconButton,
  Dialog, DialogTitle, DialogContent, DialogActions, TextField, FormControlLabel, Checkbox, Autocomplete,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import Swal from "sweetalert2";
import {
  listarTiposDocumento, gruposTipoDocumento, salvarTipoDocumento,
  ativarTipoDocumento, excluirTipoDocumento, ordenarTiposDocumento,
} from "../../services/tipoDocumentoService";

const vazio = { Id: 0, Codigo: "", Grupo: "", Descricao: "", Obrigatorio: false, PermiteMultiplos: true, Ativo: true };

export default function TipoDocumentoList() {
  const [tipos, setTipos] = useState([]);
  const [grupos, setGrupos] = useState([]);
  const [edit, setEdit] = useState(null);      // documento em edição
  const [salvando, setSalvando] = useState(false);
  const [filtro, setFiltro] = useState("");

  const carregar = useCallback(async () => {
    try {
      setTipos((await listarTiposDocumento(true)).data);
      setGrupos((await gruposTipoDocumento()).data);
    } catch (e) { Swal.fire("Erro", e?.response?.data?.mensagem || e.message, "error"); }
  }, []);
  useEffect(() => { carregar(); }, [carregar]);

  const salvar = async () => {
    if (!edit.Descricao.trim()) return Swal.fire("Atenção", "Informe a descrição do documento.", "warning");
    setSalvando(true);
    try {
      await salvarTipoDocumento(edit);
      setEdit(null);
      carregar();
    } catch (e) { Swal.fire("Erro", e?.response?.data?.mensagem || e.message, "error"); }
    finally { setSalvando(false); }
  };

  const alternar = async (t) => {
    try {
      await ativarTipoDocumento(t.Id, !t.Ativo);
      setTipos((arr) => arr.map((x) => (x.Id === t.Id ? { ...x, Ativo: !t.Ativo } : x)));
    } catch (e) { Swal.fire("Erro", e?.response?.data?.mensagem || e.message, "error"); }
  };

  const excluir = async (t) => {
    const r = await Swal.fire({
      title: "Excluir documento?",
      html: t.Anexos > 0
        ? `Há <b>${t.Anexos}</b> anexo(s) enviados neste documento — ele será <b>desativado</b> (o histórico é preservado).`
        : `"${t.Descricao}" sai do checklist da ficha.`,
      icon: "warning", showCancelButton: true, confirmButtonText: "Confirmar", cancelButtonText: "Cancelar",
    });
    if (!r.isConfirmed) return;
    try {
      const { data } = await excluirTipoDocumento(t.Id);
      Swal.fire({ toast: true, position: "top-end", icon: "success", title: data.mensagem, showConfirmButton: false, timer: 2200 });
      carregar();
    } catch (e) { Swal.fire("Erro", e?.response?.data?.mensagem || e.message, "error"); }
  };

  // Sobe/desce na lista e grava a nova ordem.
  const mover = async (i, delta) => {
    const nova = [...tipos];
    const destino = i + delta;
    if (destino < 0 || destino >= nova.length) return;
    [nova[i], nova[destino]] = [nova[destino], nova[i]];
    setTipos(nova);
    try { await ordenarTiposDocumento(nova.map((t) => t.Id)); }
    catch (e) { Swal.fire("Erro", e?.response?.data?.mensagem || e.message, "error"); carregar(); }
  };

  const termo = filtro.trim().toLowerCase();
  const visiveis = termo
    ? tipos.filter((t) => `${t.Descricao} ${t.Grupo} ${t.Codigo}`.toLowerCase().includes(termo))
    : tipos;
  const ativos = tipos.filter((t) => t.Ativo).length;
  const obrigatorios = tipos.filter((t) => t.Ativo && t.Obrigatorio).length;

  return (
    <Box>
      <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2} flexWrap="wrap" useFlexGap>
        <Box>
          <Typography variant="h5">Documentos da ficha</Typography>
          <Typography variant="body2" color="text.secondary">
            O checklist da ficha monta a partir desta lista — cadastrou, aparece; desativou, some.
          </Typography>
        </Box>
        <Button variant="contained" startIcon={<AddIcon />} onClick={() => setEdit({ ...vazio })}>Novo documento</Button>
      </Stack>

      <Stack direction="row" spacing={1} mb={1.5} flexWrap="wrap" useFlexGap alignItems="center">
        <TextField size="small" label="Buscar" value={filtro} onChange={(e) => setFiltro(e.target.value)} sx={{ minWidth: 260 }} />
        <Chip label={`${ativos} ativos`} color="success" variant="outlined" />
        <Chip label={`${obrigatorios} obrigatórios`} color="primary" variant="outlined" />
        <Chip label={`${tipos.length - ativos} inativos`} variant="outlined" />
      </Stack>

      <Alert severity="info" sx={{ mb: 2 }}>
        <b>Obrigatório</b> trava o envio da ficha enquanto o candidato não anexar o documento.
        <b> Desativar</b> tira do checklist sem apagar o que já foi enviado.
      </Alert>

      <Paper>
        <TableContainer sx={{ maxHeight: "65vh" }}>
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell width={90} align="center">Ordem</TableCell>
                <TableCell>Grupo</TableCell>
                <TableCell>Documento</TableCell>
                <TableCell align="center">Exigência</TableCell>
                <TableCell align="center">Vários arquivos</TableCell>
                <TableCell align="center">Anexos</TableCell>
                <TableCell align="center">Ativo</TableCell>
                <TableCell align="center" sx={{ minWidth: 110 }}>Ações</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {visiveis.map((t) => {
                const i = tipos.indexOf(t);
                return (
                  <TableRow key={t.Id} hover sx={{ opacity: t.Ativo ? 1 : 0.55 }}>
                    <TableCell align="center">
                      <Stack direction="row" justifyContent="center">
                        <IconButton size="small" disabled={!!termo || i === 0} onClick={() => mover(i, -1)}>
                          <ArrowUpwardIcon fontSize="inherit" />
                        </IconButton>
                        <IconButton size="small" disabled={!!termo || i === tipos.length - 1} onClick={() => mover(i, 1)}>
                          <ArrowDownwardIcon fontSize="inherit" />
                        </IconButton>
                      </Stack>
                    </TableCell>
                    <TableCell>{t.Grupo || "Outros"}</TableCell>
                    <TableCell>
                      <Typography variant="body2">{t.Descricao}</Typography>
                      <Typography variant="caption" color="text.disabled">{t.Codigo}</Typography>
                    </TableCell>
                    <TableCell align="center">
                      {t.Obrigatorio
                        ? <Chip size="small" color="primary" label="Obrigatório" />
                        : <Chip size="small" variant="outlined" label="Opcional" />}
                    </TableCell>
                    <TableCell align="center">{t.PermiteMultiplos ? "Sim" : "Não"}</TableCell>
                    <TableCell align="center">{t.Anexos || "—"}</TableCell>
                    <TableCell align="center">
                      <Tooltip title={t.Ativo ? "Desativar (some do checklist)" : "Reativar"}>
                        <Switch size="small" checked={t.Ativo} onChange={() => alternar(t)} />
                      </Tooltip>
                    </TableCell>
                    <TableCell align="center" sx={{ whiteSpace: "nowrap" }}>
                      <Tooltip title="Editar"><IconButton size="small" onClick={() => setEdit({ ...t })}><EditIcon fontSize="small" /></IconButton></Tooltip>
                      <Tooltip title="Excluir"><IconButton size="small" color="error" onClick={() => excluir(t)}><DeleteIcon fontSize="small" /></IconButton></Tooltip>
                    </TableCell>
                  </TableRow>
                );
              })}
              {visiveis.length === 0 && (
                <TableRow><TableCell colSpan={8} align="center">Nenhum documento cadastrado.</TableCell></TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {/* Cadastro / edição */}
      <Dialog open={!!edit} onClose={() => setEdit(null)} maxWidth="sm" fullWidth>
        <DialogTitle>{edit?.Id ? "Editar documento" : "Novo documento"}</DialogTitle>
        <DialogContent dividers>
          {edit && (
            <Stack spacing={2} sx={{ mt: 0.5 }}>
              <TextField fullWidth multiline minRows={2} label="Descrição (é o texto que o candidato lê)"
                value={edit.Descricao} onChange={(e) => setEdit({ ...edit, Descricao: e.target.value })} autoFocus />
              <Autocomplete freeSolo options={grupos} value={edit.Grupo || ""}
                onChange={(_, v) => setEdit({ ...edit, Grupo: v || "" })}
                onInputChange={(_, v) => setEdit({ ...edit, Grupo: v })}
                renderInput={(p) => <TextField {...p} label="Grupo (seção do checklist)" helperText="Ex.: Identificação, Comprovação de Renda" />} />
              <TextField select fullWidth label="Exigência" value={edit.Obrigatorio ? "S" : "N"}
                onChange={(e) => setEdit({ ...edit, Obrigatorio: e.target.value === "S" })}>
                <MenuItem value="N">Opcional — envia se for o caso</MenuItem>
                <MenuItem value="S">Obrigatório — sem ele a ficha não pode ser enviada</MenuItem>
              </TextField>
              <FormControlLabel
                control={<Checkbox checked={edit.PermiteMultiplos} onChange={(e) => setEdit({ ...edit, PermiteMultiplos: e.target.checked })} />}
                label="Permite mais de um arquivo (ex.: um por membro da família)" />
              <FormControlLabel
                control={<Checkbox checked={edit.Ativo} onChange={(e) => setEdit({ ...edit, Ativo: e.target.checked })} />}
                label="Ativo (aparece no checklist da ficha)" />
              <TextField fullWidth label="Código" value={edit.Codigo}
                onChange={(e) => setEdit({ ...edit, Codigo: e.target.value })}
                helperText="Identificador técnico — deixe vazio que o sistema gera a partir da descrição." />
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEdit(null)}>Cancelar</Button>
          <Button variant="contained" onClick={salvar} disabled={salvando}>{salvando ? "Salvando…" : "Salvar"}</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
